/**
 * DefaultOffersEditor Component
 *
 * Editor for default_offers JSONB array.
 * Allows adding/removing rewards that will be auto-assigned to new users.
 * Supports configuring override fields for user-level availability control.
 */

import React, { useState, useEffect } from 'react';
import { Select } from '@/components/common/FormField';
import type { DefaultOfferConfig } from '@/app/types/partners';
import type { RewardResponse } from '@/app/types/rewards';
import { rewardService } from '@/app/services/rewardService';

import './DefaultOffersEditor.scss';

interface DefaultOffersEditorProps {
  value: DefaultOfferConfig[] | null;
  onChange: (value: DefaultOfferConfig[] | null) => void;
  /** If true, shows read-only display without edit controls */
  readOnly?: boolean;
}

export const DefaultOffersEditor: React.FC<DefaultOffersEditorProps> = ({
  value,
  onChange,
  readOnly = false,
}) => {
  const [selectedRewardKey, setSelectedRewardKey] = useState<string>('');
  const [rewards, setRewards] = useState<RewardResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Track which offers have expanded override sections
  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());

  // Fetch ACTIVE rewards on mount
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        setIsLoading(true);
        const response = await rewardService.queryRewards({ status: 'ACTIVE', limit: 1000 });
        setRewards(response.rewards);
      } catch (err) {
        console.error('Failed to fetch rewards:', err);
        setRewards([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRewards();
  }, []);

  // All fetched rewards are already ACTIVE
  const activeRewards = rewards;

  // Get reward keys already in the list
  const existingRewardIds = new Set((value || []).map((o) => o.reward_id));

  // Available rewards (not yet added)
  const availableRewards = activeRewards.filter((r) => !existingRewardIds.has(r.reward_id));

  // Add a new default offer - structure MUST match existing database records
  const handleAdd = () => {
    if (!selectedRewardKey) return;

    const reward = activeRewards.find((r) => r.reward_id === selectedRewardKey);
    if (!reward) return;

    const newOffer: DefaultOfferConfig = {
      reward_id: reward.reward_id,
      display_priority: (value?.length || 0) + 1,
      context_json: {
        source: 'admin_manual',
        reward_key: reward.reward_key,
        reward_variant: reward.reward_variant,
        assigned_at: new Date().toISOString(),
      },
      assignment_strategy: 'MANUAL',
    };

    onChange([...(value || []), newOffer]);
    setSelectedRewardKey('');
  };

  // Remove a default offer
  const handleRemove = (rewardId: string) => {
    const updated = (value || []).filter((o) => o.reward_id !== rewardId);
    // Re-number priorities
    const renumbered = updated.map((o, idx) => ({
      ...o,
      display_priority: idx + 1,
    }));
    onChange(renumbered.length > 0 ? renumbered : null);
  };

  // Move offer up in priority
  const handleMoveUp = (index: number) => {
    if (index === 0 || !value || index >= value.length) return;
    const updated = [...value];
    const currentItem = updated[index];
    const prevItem = updated[index - 1];
    if (currentItem && prevItem) {
      updated[index - 1] = currentItem;
      updated[index] = prevItem;
      // Re-number priorities
      const renumbered = updated.map((o, idx) => ({
        ...o,
        display_priority: idx + 1,
      }));
      onChange(renumbered);
    }
  };

  // Move offer down in priority
  const handleMoveDown = (index: number) => {
    if (!value || index < 0 || index >= value.length - 1) return;
    const updated = [...value];
    const currentItem = updated[index];
    const nextItem = updated[index + 1];
    if (currentItem && nextItem) {
      updated[index] = nextItem;
      updated[index + 1] = currentItem;
      // Re-number priorities
      const renumbered = updated.map((o, idx) => ({
        ...o,
        display_priority: idx + 1,
      }));
      onChange(renumbered);
    }
  };

  // Toggle expanded state for an offer's override section
  const toggleExpanded = (rewardId: string) => {
    setExpandedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(rewardId)) {
        next.delete(rewardId);
      } else {
        next.add(rewardId);
      }
      return next;
    });
  };

  // Check if an offer has any override fields set
  const hasOverrideSet = (offer: DefaultOfferConfig): boolean => {
    return offer.context_json.available_override !== undefined && offer.context_json.available_override !== null;
  };

  // Update override fields for a specific offer
  const handleOverrideChange = (
    rewardId: string,
    field: 'available_override' | 'override_reason' | 'show_message_on_card',
    fieldValue: boolean | string | null
  ) => {
    if (!value) return;
    const updated = value.map((offer) => {
      if (offer.reward_id !== rewardId) return offer;
      return {
        ...offer,
        context_json: {
          ...offer.context_json,
          [field]: fieldValue,
        },
      };
    });
    onChange(updated);
  };

  // Clear all override fields for a specific offer
  const handleClearOverrides = (rewardId: string) => {
    if (!value) return;
    const updated = value.map((offer) => {
      if (offer.reward_id !== rewardId) return offer;
      // Remove override fields by destructuring them out
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { available_override, override_reason, show_message_on_card, ...restContext } = offer.context_json;
      return {
        ...offer,
        context_json: restContext as typeof offer.context_json,
      };
    });
    onChange(updated);
  };

  // Get display info for a reward (looks up from loaded rewards for consistency)
  const getRewardDisplayInfo = (
    offer: DefaultOfferConfig
  ): { name: string; key: string; variant: string } => {
    // Get key/variant from context_json (the actual data structure)
    const key = offer.context_json.reward_key;
    const variant = offer.context_json.reward_variant;

    // Try to find the reward in our loaded list for the display name
    const reward = rewards.find((r) => r.reward_id === offer.reward_id);
    if (reward) {
      return {
        name: reward.reward_name,
        key: key,
        variant: variant,
      };
    }

    // Fallback: use key as name if reward not in active list
    return {
      name: key,
      key: key,
      variant: variant,
    };
  };

  // Sort offers by display_priority for consistent display
  const offers = [...(value || [])].sort((a, b) => a.display_priority - b.display_priority);

  if (readOnly) {
    return (
      <div className="default-offers-editor readonly">
        {offers.length === 0 ? (
          <p className="empty-state">No default offers configured</p>
        ) : (
          <ul className="offers-list readonly">
            {offers.map((offer, index) => {
              const info = getRewardDisplayInfo(offer);
              return (
                <li key={offer.reward_id} className="offer-item">
                  <span className="priority">{index + 1}.</span>
                  <span className="offer-info-readonly">
                    <span className="reward-name">{info.name}</span>
                    <span className="reward-meta">
                      ({info.key}/{info.variant})
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="default-offers-editor">
      {/* Add new offer */}
      {!isLoading && availableRewards.length > 0 && (
        <div className="add-offer-row">
          <Select
            label="Add Default Offer"
            value={selectedRewardKey}
            onChange={setSelectedRewardKey}
            options={[
              { value: '', label: 'Select a reward...' },
              ...availableRewards
                .sort((a, b) => a.reward_name.localeCompare(b.reward_name))
                .map((r) => {
                  // Build label with distinguishing information
                  const parts: string[] = [];

                  // Add provider if available (from redemption_config)
                  if (r.redemption_config?.provider) {
                    parts.push(`(${r.redemption_config.provider})`);
                  }

                  // Add key/variant info
                  parts.push(`${r.reward_key}/${r.reward_variant}`);

                  return {
                    value: r.reward_id,
                    label: `${r.reward_name} ${parts.join(' ')}`,
                  };
                }),
            ]}
          />
          <button className="btn-add" onClick={handleAdd} disabled={!selectedRewardKey}>
            + Add
          </button>
        </div>
      )}

      {isLoading && <p className="loading-state">Loading rewards...</p>}

      {/* Current offers list */}
      {offers.length > 0 ? (
        <ul className="offers-list">
          {offers.map((offer, index) => {
            const info = getRewardDisplayInfo(offer);
            const isExpanded = expandedOffers.has(offer.reward_id);
            const hasOverride = hasOverrideSet(offer);
            return (
              <li key={offer.reward_id} className={`offer-item ${isExpanded ? 'expanded' : ''}`}>
                <div className="offer-row">
                  <div className="offer-info">
                    <span className="priority">{index + 1}.</span>
                    <span className="reward-name">{info.name}</span>
                    <span className="reward-meta">
                      ({info.key}/{info.variant})
                    </span>
                    {hasOverride && (
                      <span
                        className={`override-badge ${offer.context_json.available_override ? 'grant' : 'block'}`}
                        title={offer.context_json.override_reason || 'Override set'}
                      >
                        {offer.context_json.available_override ? 'Grant' : 'Block'}
                      </span>
                    )}
                  </div>
                  <div className="offer-actions">
                    <button
                      className={`btn-expand ${isExpanded ? 'active' : ''}`}
                      onClick={() => toggleExpanded(offer.reward_id)}
                      title={isExpanded ? 'Collapse overrides' : 'Configure overrides'}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    <button
                      className="btn-move"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      className="btn-move"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === offers.length - 1}
                      title="Move down"
                    >
                      ▼
                    </button>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemove(offer.reward_id)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {/* Override configuration panel */}
                {isExpanded && (
                  <div className="override-panel">
                    <div className="override-row">
                      <label className="override-label">
                        <input
                          type="checkbox"
                          checked={hasOverride}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Initialize with blocking override (most common use case)
                              handleOverrideChange(offer.reward_id, 'available_override', false);
                            } else {
                              handleClearOverrides(offer.reward_id);
                            }
                          }}
                        />
                        Set Availability Override
                      </label>
                    </div>
                    {hasOverride && (
                      <>
                        <div className="override-row">
                          <label className="override-label">
                            <span>Available:</span>
                            <select
                              value={offer.context_json.available_override ? 'true' : 'false'}
                              onChange={(e) =>
                                handleOverrideChange(
                                  offer.reward_id,
                                  'available_override',
                                  e.target.value === 'true'
                                )
                              }
                            >
                              <option value="false">Block (user cannot redeem)</option>
                              <option value="true">Grant (user can redeem - beta tester)</option>
                            </select>
                          </label>
                        </div>
                        <div className="override-row">
                          <label className="override-label">
                            <span>Override Message:</span>
                            <input
                              type="text"
                              value={offer.context_json.override_reason || ''}
                              onChange={(e) =>
                                handleOverrideChange(
                                  offer.reward_id,
                                  'override_reason',
                                  e.target.value || null
                                )
                              }
                              placeholder="e.g., Please provide your shipping address"
                            />
                          </label>
                        </div>
                        <div className="override-row">
                          <label className="override-label">
                            <input
                              type="checkbox"
                              checked={offer.context_json.show_message_on_card ?? false}
                              onChange={(e) =>
                                handleOverrideChange(
                                  offer.reward_id,
                                  'show_message_on_card',
                                  e.target.checked
                                )
                              }
                            />
                            Show message on reward card
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="empty-state">
          No default offers configured. New users will not have any offers automatically assigned.
        </p>
      )}

      {availableRewards.length === 0 && offers.length > 0 && !isLoading && (
        <p className="all-added-state">All available rewards have been added.</p>
      )}
    </div>
  );
};

export default DefaultOffersEditor;
